// SPDX-License-Identifier: AGPL-3.0-or-later

import {action, makeObservable, observable, observableRef, when} from 'mobx';

export interface VoiceChannelInterestEngine {
	readonly connectedChannelId: string | null;
	readonly currentUserId: string | null;
	isLargeGuild(guildId: string): boolean;
	largeGuildIds(): ReadonlyArray<string>;
	purgeGuild(guildId: string, accepts: (channelId: string, userId: string) => boolean): void;
}

const EMPTY_CHANNEL_IDS: ReadonlyArray<string> = Object.freeze([]);
const DIRECT_JOIN_SNAPSHOT_TIMEOUT_MS = 1_500;

class VoiceChannelInterest {
	enabled = false;
	engine: VoiceChannelInterestEngine | null = null;
	private listChannels: ReadonlyMap<string, ReadonlyArray<string>> = new Map();
	private retainedChannels: ReadonlyMap<string, ReadonlyMap<string, number>> = new Map();
	private acceptedChannels: ReadonlyMap<string, ReadonlySet<string>> = new Map();
	private passiveVersions: ReadonlyMap<string, number> = new Map();

	constructor() {
		makeObservable<
			this,
			'listChannels' | 'retainedChannels' | 'acceptedChannels' | 'passiveVersions' | 'changeRetained'
		>(this, {
			enabled: observable,
			listChannels: observableRef,
			retainedChannels: observableRef,
			acceptedChannels: observableRef,
			passiveVersions: observableRef,
			handleRollout: action,
			setListChannels: action,
			changeRetained: action,
			setAcceptedChannels: action,
			resetAccepted: action,
			notePassiveVoiceStates: action,
		});
	}

	handleRollout(data: unknown): void {
		this.enabled = (data as {voice_channel_interest?: unknown} | null | undefined)?.voice_channel_interest === true;
	}

	attachEngine(engine: VoiceChannelInterestEngine): void {
		this.engine = engine;
	}

	setListChannels(guildId: string, channelIds: ReadonlyArray<string>): void {
		const current = this.listChannels.get(guildId) ?? EMPTY_CHANNEL_IDS;
		if (current.length === channelIds.length && current.every((id, index) => id === channelIds[index])) return;
		const next = new Map(this.listChannels);
		if (channelIds.length === 0) {
			next.delete(guildId);
		} else {
			next.set(guildId, [...channelIds]);
		}
		this.listChannels = next;
	}

	getListChannels(guildId: string): ReadonlyArray<string> {
		return this.listChannels.get(guildId) ?? EMPTY_CHANNEL_IDS;
	}

	retainChannel(guildId: string, channelId: string): () => void {
		this.changeRetained(guildId, channelId, 1);
		let released = false;
		return () => {
			if (released) return;
			released = true;
			this.changeRetained(guildId, channelId, -1);
		};
	}

	getRetainedChannels(guildId: string): ReadonlyArray<string> {
		const channels = this.retainedChannels.get(guildId);
		return channels ? [...channels.keys()] : EMPTY_CHANNEL_IDS;
	}

	accepts(guildId: string | null, channelId: string | null, userId: string | null): boolean {
		if (guildId == null || channelId == null) return true;
		const accepted = this.acceptedChannels.get(guildId);
		if (accepted == null || accepted.has(channelId)) return true;
		const engine = this.engine;
		return channelId === engine?.connectedChannelId || (userId != null && userId === engine?.currentUserId);
	}

	setAcceptedChannels(guildId: string, channelIds: Iterable<string> | null): void {
		const next = new Map(this.acceptedChannels);
		if (channelIds == null) {
			next.delete(guildId);
		} else {
			next.set(guildId, new Set(channelIds));
		}
		this.acceptedChannels = next;
		if (channelIds != null) this.purge(guildId);
	}

	resetAccepted(): void {
		this.acceptedChannels = new Map();
	}

	purge(guildId: string): void {
		if (!this.acceptedChannels.has(guildId)) return;
		this.engine?.purgeGuild(guildId, (channelId, userId) => this.accepts(guildId, channelId, userId));
	}

	notePassiveVoiceStates(guildId: string): void {
		this.passiveVersions = new Map(this.passiveVersions).set(guildId, (this.passiveVersions.get(guildId) ?? 0) + 1);
	}

	async awaitChannelState(guildId: string | null, channelId: string): Promise<void> {
		if (guildId == null || this.accepts(guildId, channelId, null)) return;
		const release = this.retainChannel(guildId, channelId);
		let versionWhenSent: number | null = null;
		try {
			await when(
				() => {
					const version = this.passiveVersions.get(guildId) ?? 0;
					if (versionWhenSent == null) {
						if (this.acceptedChannels.get(guildId)?.has(channelId)) versionWhenSent = version;
						return false;
					}
					return version !== versionWhenSent;
				},
				{timeout: DIRECT_JOIN_SNAPSHOT_TIMEOUT_MS},
			);
		} catch {
			return;
		} finally {
			release();
		}
	}

	private changeRetained(guildId: string, channelId: string, delta: number): void {
		const channels = new Map(this.retainedChannels.get(guildId));
		const count = (channels.get(channelId) ?? 0) + delta;
		if (count > 0) {
			channels.set(channelId, count);
		} else {
			channels.delete(channelId);
		}
		const next = new Map(this.retainedChannels);
		if (channels.size > 0) {
			next.set(guildId, channels);
		} else {
			next.delete(guildId);
		}
		this.retainedChannels = next;
	}
}

export default new VoiceChannelInterest();
